import { fetchAllRows } from '@kit/shared/lib/fetch-all-rows';
import { captureServiceError } from '@kit/shared/lib/sentry-helpers';
import { Result, ServiceError } from '@kit/shared/result';
import { SignedUrlExpiry, StorageBuckets } from '@kit/shared/storage';

import { setRowShownOnProfile } from './dancer-media-visibility';
import type { DancerMediaContext } from './dancer-media.context';
import { buildMediaMetadataUpdates } from './dancer-media.mappers';
import type { DancerMedia } from './dancer-media.schema';

/**
 * Get all media for a user with signed URLs for images
 */
export async function getMediaByUser(
  ctx: DancerMediaContext,
  userId: string,
): Promise<Result<DancerMedia[]>> {
  try {
    const { data, error } = await fetchAllRows((from, to) =>
      ctx.client
        .from('artist_media')
        .select('*')
        .eq('user_id', userId)
        // Rows whose file is gone (lost_at) stay hidden unless they are
        // waiting for a re-upload, same rule as the public readers.
        .or('lost_at.is.null,requires_reupload.eq.true')
        .order('display_order', { ascending: true })
        .order('created_at', { ascending: false })
        .order('id')
        .range(from, to),
    );

    if (error) {
      return Result.fail(ServiceError.database(error.message, error));
    }

    if (!data || data.length === 0) {
      return Result.ok([]);
    }

    // Sign all media with a storage_path in ONE batch round-trip (no
    // per-row sign loop). Videos added as YouTube/Vimeo links
    // (storage_path null, external_url set) skip this step; uploaded
    // videos (storage_path set) get a signed URL just like images.
    const pathsToSign = data
      .map((item) => item.storage_path)
      .filter((p): p is string => !!p);

    const signedByPath = new Map<string, string>();
    if (pathsToSign.length > 0) {
      const batch = await ctx.storageService.getBatchSignedUrls(
        StorageBuckets.DANCER_MEDIA,
        pathsToSign,
        { expiresIn: SignedUrlExpiry.IMMEDIATE_DISPLAY },
      );
      for (const { path, signedUrl } of batch) {
        if (signedUrl) signedByPath.set(path, signedUrl);
      }
    }

    const mediaWithUrls = data.map((item) => {
      const signed = item.storage_path
        ? signedByPath.get(item.storage_path)
        : undefined;
      return signed ? { ...item, storage_path: signed } : item;
    });

    return Result.ok(mediaWithUrls);
  } catch (error) {
    const serviceError = ServiceError.internal(
      error instanceof Error ? error.message : 'Unknown error',
      error,
    );
    captureServiceError(serviceError, {
      action: 'getMediaByUser',
      step: 'execution',
    });
    return Result.fail(serviceError);
  }
}

/**
 * Get headshot for a user
 */
export async function getHeadshot(
  ctx: DancerMediaContext,
  userId: string,
): Promise<Result<DancerMedia | null>> {
  try {
    const { data, error } = await ctx.client
      .from('artist_media')
      .select('*')
      .eq('user_id', userId)
      .eq('media_type', 'headshot')
      .maybeSingle();

    if (error) {
      return Result.fail(ServiceError.database(error.message, error));
    }

    return Result.ok(data);
  } catch (error) {
    const serviceError = ServiceError.internal(
      error instanceof Error ? error.message : 'Unknown error',
      error,
    );
    captureServiceError(serviceError, {
      action: 'getHeadshot',
      step: 'execution',
    });
    return Result.fail(serviceError);
  }
}

/**
 * Update media metadata (title, description, editorial category).
 *
 * `category` is the spec-vocabulary editorial bucket. Pair-validation
 * (photo vs video values) is enforced at the DB layer by
 * `artist_media_category_check`; passing an invalid pair returns a
 * `Result.fail` with the constraint message.
 */
export async function updateMedia(
  ctx: DancerMediaContext,
  mediaId: string,
  input: {
    title?: string | null;
    description?: string | null;
    category?: string | null;
  },
): Promise<Result<DancerMedia>> {
  try {
    const updates = buildMediaMetadataUpdates(input);

    const { data, error } = await ctx.client
      .from('artist_media')
      .update(updates)
      .eq('id', mediaId)
      .select()
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        return Result.fail(ServiceError.notFound('Media not found'));
      }
      return Result.fail(ServiceError.database(error.message, error));
    }

    return Result.ok(data);
  } catch (error) {
    const serviceError = ServiceError.internal(
      error instanceof Error ? error.message : 'Unknown error',
      error,
    );
    captureServiceError(serviceError, {
      action: 'updateMedia',
      step: 'execution',
    });
    return Result.fail(serviceError);
  }
}

/**
 * Show or hide one item on the public profile. Hiding removes the public R2
 * copies (see dancer-media-visibility.ts); showing restores the image copy.
 * RLS decides who may touch the row: a caller who cannot read it gets
 * not found.
 */
export async function setShownOnProfile(
  ctx: DancerMediaContext,
  mediaId: string,
  shown: boolean,
): Promise<Result<void>> {
  try {
    const { data: row, error } = await ctx.client
      .from('artist_media')
      .select('*')
      .eq('id', mediaId)
      .maybeSingle();
    if (error) return Result.fail(ServiceError.database(error.message, error));
    if (!row) return Result.fail(ServiceError.notFound('Media not found'));

    const result = await setRowShownOnProfile(
      ctx.client,
      () => ctx.getR2Service(),
      row,
      shown,
    );
    if (result.isFailure) {
      captureServiceError(result.getError(), {
        action: 'setShownOnProfile',
        step: 'execution',
      });
    }
    return result;
  } catch (error) {
    const serviceError = ServiceError.internal(
      error instanceof Error ? error.message : 'Unknown error',
      error,
    );
    captureServiceError(serviceError, {
      action: 'setShownOnProfile',
      step: 'execution',
    });
    return Result.fail(serviceError);
  }
}

/**
 * Finish hiding a video that was made private while Mux was still fetching
 * its R2 source. `setRowShownOnProfile` keeps that source until the asset
 * leaves `preparing`; the Mux webhook calls this once it is ready or errored.
 * A shown row, or one still preparing, is left alone.
 */
export async function removeDeferredPublicCopies(
  ctx: DancerMediaContext,
  mediaId: string,
): Promise<Result<void>> {
  const { data: row, error } = await ctx.client
    .from('artist_media')
    .select('*')
    .eq('id', mediaId)
    .maybeSingle();
  if (error) return Result.fail(ServiceError.database(error.message, error));
  if (!row || row.shown_on_profile || row.mux_status === 'preparing') {
    return Result.ok(undefined);
  }
  return setRowShownOnProfile(ctx.client, () => ctx.getR2Service(), row, false);
}

/**
 * Persist a new display order for a set of media rows owned by `userId`.
 *
 * `items` is the new ordered list. The first item gets `display_order = 0`,
 * the second `1`, and so on. Each row update is gated on `user_id` so RLS
 * prevents cross-user writes even if `mediaId`s leak.
 */
export async function reorder(
  ctx: DancerMediaContext,
  userId: string,
  items: { id: string }[],
): Promise<Result<void>> {
  if (items.length === 0) return Result.ok(undefined);

  try {
    const updates = items.map((item, index) =>
      ctx.client
        .from('artist_media')
        .update({ display_order: index })
        .eq('id', item.id)
        .eq('user_id', userId),
    );

    const results = await Promise.all(updates);
    const firstError = results.find((r) => r.error)?.error;
    if (firstError) {
      return Result.fail(ServiceError.database(firstError.message, firstError));
    }
    return Result.ok(undefined);
  } catch (error) {
    const serviceError = ServiceError.internal(
      error instanceof Error ? error.message : 'Unknown error',
      error,
    );
    captureServiceError(serviceError, {
      action: 'reorder',
      step: 'execution',
    });
    return Result.fail(serviceError);
  }
}
